import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'
import { useSearchParams } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { Select } from '@base-ui-components/react/select'
import { Form } from '@base-ui-components/react/form'
import { Field } from '@base-ui-components/react/field'

import Button from '@/components/ui/Button'
import Spinner from '@/components/Spinner'
import { t } from '@/lib/i18n'
import type { SetupFormData } from '@/lib/project/types'

import { fetchUserGitHubRepos, GitHubRepo } from '@/lib/github/common'
import { initProject } from '@/lib/project/server'

const ProjectSetupForm = () => {
  const { data: session } = useSession()
  const [loading, setLoading] = useState(false)
  const [repos, setRepos] = useState<GitHubRepo[]>([])
  const searchParams = useSearchParams()

  const onSubmit = (values: SetupFormData): void => {
    setLoading(true)
    initProject({
      ...values,
      name: values.repoUrl.split('/').pop() as string
    }).then(project => {
      setLoading(false)
      const params = new URLSearchParams(searchParams.toString())
      params.delete('setup')
      params.set('threadId', project?.threads[0].id ?? '')
      window.history.pushState(null, '', `?${params.toString()}`)
    })
  }

  useEffect(() => {
    const fetchRepos = async () => {
      try {
        const token = session?.user.accounts[0].access_token || ''
        const fetchedRepos = await fetchUserGitHubRepos(token)
        setRepos(fetchedRepos)
      } catch (error) {
        console.error('Failed to fetch GitHub repos:', error)
      }
    }

    if (session) {
      fetchRepos()
    }
  }, [session])

  const { register, handleSubmit, watch, setValue } = useForm<SetupFormData>()
  const repoUrl = watch('repoUrl')
  const aiProvider = watch('aiProvider')
  const openaiApiKey = watch('openaiApiKey')
  const ollamaEndpointUrl = watch('ollamaEndpointUrl')
  const ramSizeGb = watch('ramSizeGb')

  return (
    <Form
      className='flex w-full max-w-64 flex-col gap-4'
      // errors={errors}
      // onClearErrors={setErrors}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Field.Root
        {...register('repoUrl', { required: true })}
        className='flex flex-col items-start gap-1'
      >
        <Field.Label className='text-sm font-medium text-gray-900'>
          {t('Project:')}
        </Field.Label>
        <Select.Root
          value={repoUrl ?? ''}
          onValueChange={e => setValue('repoUrl', e)}
        >
          <Select.Trigger className='border border-gray-200 rounded-sm p-[.375rem] flex h-10 w-full items-center justify-between rounded-sm px-[0.75rem] font-[inherit] text-sm font-medium text-gray-600 select-none hover:bg-gray-100 focus-visible:bg-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-blue-800 active:bg-gray-200 data-[pressed]:bg-gray-100 data-[pressed]:text-gray-900'>
            <Select.Value placeholder='Select a repository' />
            <Select.Icon />
          </Select.Trigger>
          <Select.Portal>
            <Select.Positioner className='outline-none' sideOffset={8}>
              <Select.ScrollUpArrow className="top-0 z-[1] flex h-4 w-full cursor-default items-center justify-center rounded-md bg-[canvas] text-center text-xs before:absolute before:top-[-100%] before:left-0 before:h-full before:w-full before:content-[''] data-[direction=down]:bottom-0 data-[direction=down]:before:bottom-[-100%]" />
              <Select.Popup className='group origin-[var(--transform-origin)] rounded-md bg-[canvas] py-1 text-gray-900 shadow-lg shadow-gray-200 outline outline-1 outline-gray-200 transition-[transform,scale,opacity] data-[ending-style]:scale-90 data-[ending-style]:scale-100 data-[ending-style]:opacity-0 data-[ending-style]:opacity-100 data-[ending-style]:transition-none data-[starting-style]:scale-90 data-[starting-style]:opacity-0 data-[side=none]:data-[starting-style]:scale-100 data-[side=none]:data-[starting-style]:opacity-100 data-[side=none]:data-[starting-style]:transition-none dark:shadow-none dark:-outline-offset-1 dark:outline-gray-300'>
                {repos.map(repo => (
                  <Select.Item
                    className='grid min-w-[var(--anchor-width)] cursor-pointer items-center gap-2 py-2 pr-4 pl-2.5 text-sm leading-4 outline-none select-none group-data-[side=none]:min-w-[calc(var(--anchor-width)+1rem)] group-data-[side=none]:pr-12 group-data-[side=none]:text-base group-data-[side=none]:leading-4 data-[highlighted]:relative data-[highlighted]:z-0 data-[highlighted]:text-gray-50 data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-1 data-[highlighted]:before:inset-y-0 data-[highlighted]:before:z-[-1] data-[highlighted]:before:rounded-sm data-[highlighted]:before:bg-gray-900'
                    key={repo.id}
                    value={repo.html_url}
                  >
                    {repo.full_name}
                  </Select.Item>
                ))}
              </Select.Popup>
              <Select.ScrollDownArrow className="bottom-0 z-[1] flex h-4 w-full cursor-default items-center justify-center rounded-md bg-[canvas] text-center text-xs before:absolute before:top-[-100%] before:left-0 before:h-full before:w-full before:content-[''] data-[direction=down]:bottom-0 data-[direction=down]:before:bottom-[-100%]" />
            </Select.Positioner>
          </Select.Portal>
        </Select.Root>
        <Field.Error className='text-sm text-red-800' />
      </Field.Root>
      {repoUrl && (
        <>
          <Field.Root
            {...register('aiProvider', { required: true })}
            className='flex flex-col items-start gap-1'
          >
            <Field.Label className='text-sm font-medium text-gray-900'>
              {t('AI provider:')}
            </Field.Label>
            <Select.Root
              value={aiProvider ?? 'Ollama'}
              onValueChange={e => setValue('aiProvider', e)}
            >
              <Select.Trigger className='border border-gray-200 rounded-sm p-[.375rem] flex h-10 w-full items-center justify-between rounded-sm px-[0.75rem] font-[inherit] text-sm font-medium text-gray-600 select-none hover:bg-gray-100 focus-visible:bg-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-blue-800 active:bg-gray-200 data-[pressed]:bg-gray-100 data-[pressed]:text-gray-900'>
                <Select.Value placeholder='Choose AI provider' />
                <Select.Icon />
              </Select.Trigger>
              <Select.Portal>
                <Select.Positioner className='outline-none' sideOffset={8}>
                  <Select.ScrollUpArrow className="top-0 z-[1] flex h-4 w-full cursor-default items-center justify-center rounded-md bg-[canvas] text-center text-xs before:absolute before:top-[-100%] before:left-0 before:h-full before:w-full before:content-[''] data-[direction=down]:bottom-0 data-[direction=down]:before:bottom-[-100%]" />
                  <Select.Popup className='group origin-[var(--transform-origin)] rounded-md bg-[canvas] py-1 text-gray-900 shadow-lg shadow-gray-200 outline outline-1 outline-gray-200 transition-[transform,scale,opacity] data-[ending-style]:scale-90 data-[ending-style]:scale-100 data-[ending-style]:opacity-0 data-[ending-style]:opacity-100 data-[ending-style]:transition-none data-[starting-style]:scale-90 data-[starting-style]:opacity-0 data-[side=none]:data-[starting-style]:scale-100 data-[side=none]:data-[starting-style]:opacity-100 data-[side=none]:data-[starting-style]:transition-none dark:shadow-none dark:-outline-offset-1 dark:outline-gray-300'>
                    <Select.Item
                      className='grid min-w-[var(--anchor-width)] cursor-pointer items-center gap-2 py-2 pr-4 pl-2.5 text-sm leading-4 outline-none select-none group-data-[side=none]:min-w-[calc(var(--anchor-width)+1rem)] group-data-[side=none]:pr-12 group-data-[side=none]:text-base group-data-[side=none]:leading-4 data-[highlighted]:relative data-[highlighted]:z-0 data-[highlighted]:text-gray-50 data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-1 data-[highlighted]:before:inset-y-0 data-[highlighted]:before:z-[-1] data-[highlighted]:before:rounded-sm data-[highlighted]:before:bg-gray-900'
                      value='Ollama'
                    >
                      Ollama - (Open Source)
                    </Select.Item>
                    <Select.Item
                      className='grid min-w-[var(--anchor-width)] cursor-pointer items-center gap-2 py-2 pr-4 pl-2.5 text-sm leading-4 outline-none select-none group-data-[side=none]:min-w-[calc(var(--anchor-width)+1rem)] group-data-[side=none]:pr-12 group-data-[side=none]:text-base group-data-[side=none]:leading-4 data-[highlighted]:relative data-[highlighted]:z-0 data-[highlighted]:text-gray-50 data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-1 data-[highlighted]:before:inset-y-0 data-[highlighted]:before:z-[-1] data-[highlighted]:before:rounded-sm data-[highlighted]:before:bg-gray-900'
                      value='OpenAI'
                    >
                      OpenAI - (Premium feature)
                    </Select.Item>
                  </Select.Popup>
                  <Select.ScrollDownArrow className="bottom-0 z-[1] flex h-4 w-full cursor-default items-center justify-center rounded-md bg-[canvas] text-center text-xs before:absolute before:top-[-100%] before:left-0 before:h-full before:w-full before:content-[''] data-[direction=down]:bottom-0 data-[direction=down]:before:bottom-[-100%]" />
                </Select.Positioner>
              </Select.Portal>
            </Select.Root>
            <Field.Error className='text-sm text-red-800' />
          </Field.Root>
          <Field.Root
            {...register('devBranch', { required: false })}
            className='flex flex-col items-start gap-1'
          >
            <Field.Label className='text-sm font-medium text-gray-900'>
              {t('Development branch:')}
            </Field.Label>
            <Field.Control
              required
              defaultValue=''
              className='h-10 w-full rounded-md border border-gray-200 pl-3.5 text-base text-gray-900 focus:outline focus:outline-2 focus:-outline-offset-1 focus:outline-blue-800'
            />
            <Field.Error className='text-sm text-red-800' />
          </Field.Root>
        </>
      )}
      {aiProvider === 'Ollama' && (
        <>
          <Field.Root
            {...register('ollamaEndpointUrl', { required: true })}
            className='flex flex-col items-start gap-1'
          >
            <Field.Label className='text-sm font-medium text-gray-900'>
              {t('Ollama endpoint:')}
            </Field.Label>
            <Field.Control
              required
              defaultValue=''
              className='h-10 w-full rounded-md border border-gray-200 pl-3.5 text-base text-gray-900 focus:outline focus:outline-2 focus:-outline-offset-1 focus:outline-blue-800'
            />
            <Field.Error className='text-sm text-red-800' />
          </Field.Root>
          <Field.Root
            {...register('ramSizeGb', { required: true })}
            className='flex flex-col items-start gap-1'
          >
            <Field.Label className='text-sm font-medium text-gray-900'>
              {t('Available VRAM (GB):')}
            </Field.Label>
            <Select.Root
              value={ramSizeGb ?? '5'}
              onValueChange={e => setValue('ramSizeGb', e)}
            >
              <Select.Trigger className='border border-gray-200 rounded-sm p-[.375rem] flex h-10 w-full items-center justify-between rounded-sm px-[0.75rem] font-[inherit] text-sm font-medium text-gray-600 select-none hover:bg-gray-100 focus-visible:bg-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-blue-800 active:bg-gray-200 data-[pressed]:bg-gray-100 data-[pressed]:text-gray-900'>
                <Select.Value placeholder='Choose AI provider' />
                <Select.Icon />
              </Select.Trigger>
              <Select.Portal>
                <Select.Positioner className='outline-none' sideOffset={8}>
                  <Select.ScrollUpArrow className="top-0 z-[1] flex h-4 w-full cursor-default items-center justify-center rounded-md bg-[canvas] text-center text-xs before:absolute before:top-[-100%] before:left-0 before:h-full before:w-full before:content-[''] data-[direction=down]:bottom-0 data-[direction=down]:before:bottom-[-100%]" />
                  <Select.Popup className='group origin-[var(--transform-origin)] rounded-md bg-[canvas] py-1 text-gray-900 shadow-lg shadow-gray-200 outline outline-1 outline-gray-200 transition-[transform,scale,opacity] data-[ending-style]:scale-90 data-[ending-style]:scale-100 data-[ending-style]:opacity-0 data-[ending-style]:opacity-100 data-[ending-style]:transition-none data-[starting-style]:scale-90 data-[starting-style]:opacity-0 data-[side=none]:data-[starting-style]:scale-100 data-[side=none]:data-[starting-style]:opacity-100 data-[side=none]:data-[starting-style]:transition-none dark:shadow-none dark:-outline-offset-1 dark:outline-gray-300'>
                    <Select.Item
                      className='grid min-w-[var(--anchor-width)] cursor-pointer items-center gap-2 py-2 pr-4 pl-2.5 text-sm leading-4 outline-none select-none group-data-[side=none]:min-w-[calc(var(--anchor-width)+1rem)] group-data-[side=none]:pr-12 group-data-[side=none]:text-base group-data-[side=none]:leading-4 data-[highlighted]:relative data-[highlighted]:z-0 data-[highlighted]:text-gray-50 data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-1 data-[highlighted]:before:inset-y-0 data-[highlighted]:before:z-[-1] data-[highlighted]:before:rounded-sm data-[highlighted]:before:bg-gray-900'
                      value={5}
                    >
                      Up to 5GB
                    </Select.Item>
                    <Select.Item
                      className='grid min-w-[var(--anchor-width)] cursor-pointer items-center gap-2 py-2 pr-4 pl-2.5 text-sm leading-4 outline-none select-none group-data-[side=none]:min-w-[calc(var(--anchor-width)+1rem)] group-data-[side=none]:pr-12 group-data-[side=none]:text-base group-data-[side=none]:leading-4 data-[highlighted]:relative data-[highlighted]:z-0 data-[highlighted]:text-gray-50 data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-1 data-[highlighted]:before:inset-y-0 data-[highlighted]:before:z-[-1] data-[highlighted]:before:rounded-sm data-[highlighted]:before:bg-gray-900'
                      value={10}
                    >
                      Up to 10GB
                    </Select.Item>
                    <Select.Item
                      className='grid min-w-[var(--anchor-width)] cursor-pointer items-center gap-2 py-2 pr-4 pl-2.5 text-sm leading-4 outline-none select-none group-data-[side=none]:min-w-[calc(var(--anchor-width)+1rem)] group-data-[side=none]:pr-12 group-data-[side=none]:text-base group-data-[side=none]:leading-4 data-[highlighted]:relative data-[highlighted]:z-0 data-[highlighted]:text-gray-50 data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-1 data-[highlighted]:before:inset-y-0 data-[highlighted]:before:z-[-1] data-[highlighted]:before:rounded-sm data-[highlighted]:before:bg-gray-900'
                      value={20}
                    >
                      Up to 20GB
                    </Select.Item>
                  </Select.Popup>
                  <Select.ScrollDownArrow className="bottom-0 z-[1] flex h-4 w-full cursor-default items-center justify-center rounded-md bg-[canvas] text-center text-xs before:absolute before:top-[-100%] before:left-0 before:h-full before:w-full before:content-[''] data-[direction=down]:bottom-0 data-[direction=down]:before:bottom-[-100%]" />
                </Select.Positioner>
              </Select.Portal>
            </Select.Root>
            <Field.Error className='text-sm text-red-800' />
          </Field.Root>
        </>
      )}
      {aiProvider === 'OpenAI' && (
        <Field.Root
          {...register('openaiApiKey', { required: true })}
          className='flex flex-col items-start gap-1'
        >
          <Field.Label className='text-sm font-medium text-gray-900'>
            {t('OpenAI API Key:')}
          </Field.Label>
          <Field.Control
            required
            defaultValue=''
            className='h-10 w-full rounded-md border border-gray-200 pl-3.5 text-base text-gray-900 focus:outline focus:outline-2 focus:-outline-offset-1 focus:outline-blue-800'
          />
          <Field.Error className='text-sm text-red-800' />
        </Field.Root>
      )}
      {aiProvider && (
        <Button
          disabled={
            loading || aiProvider === 'OpenAI'
              ? !openaiApiKey
              : !ollamaEndpointUrl
          }
          type='submit'
        >
          {loading ? <Spinner className='h-6 w-6' /> : 'Setup project'}
        </Button>
      )}
    </Form>
  )
}

export default ProjectSetupForm
